import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { range } from 'utils/arrayUtils';
import SelectField from 'components/forms/selectField/SelectField';
import PropertyFormFieldLabel from 'pages/property/common/PropertyFormFieldLabel';
import { PropertySettingsMainTabFormValues } from '../../PropertySettingsMainTab.schema';
import { CHECK_IN_TIME_END_FLEXIBLE } from './AirbnbSettings.constants';

const CheckInTimeEndField = () => {
  const { t } = useTranslation();
  const { setValue, watch } =
    useFormContext<PropertySettingsMainTabFormValues>();
  const checkInTimeStartFlexible = watch(
    'airbnbSettings.checkInTimeStartFlexible',
  );
  const checkInTimeEnd = watch('airbnbSettings.checkInTimeEnd');
  const bookingCheckInTime = Number(watch('bookingSettings.checkInTime'));

  const isStartTimeFlexible =
    checkInTimeStartFlexible === 'true' || checkInTimeStartFlexible === true;

  // set checkInTimeEnd when switching to flexible mode
  useEffect(() => {
    if (isStartTimeFlexible) {
      setValue('airbnbSettings.checkInTimeEnd', CHECK_IN_TIME_END_FLEXIBLE);
    } else if (
      checkInTimeEnd === CHECK_IN_TIME_END_FLEXIBLE &&
      !isStartTimeFlexible
    ) {
      // When switching from flexible to default, set to flexible as default option
      setValue('airbnbSettings.checkInTimeEnd', CHECK_IN_TIME_END_FLEXIBLE, {
        shouldDirty: true,
      });
    }
  }, [isStartTimeFlexible, checkInTimeEnd, setValue]);

  // clear end time if it becomes invalid due to booking settings change
  useEffect(() => {
    if (
      !isStartTimeFlexible &&
      checkInTimeEnd &&
      checkInTimeEnd !== CHECK_IN_TIME_END_FLEXIBLE
    ) {
      const minEndTime = (bookingCheckInTime || 15) + 2;
      const endTimeValue =
        typeof checkInTimeEnd === 'string'
          ? parseInt(checkInTimeEnd, 10)
          : checkInTimeEnd;

      if (endTimeValue < minEndTime) {
        setValue('airbnbSettings.checkInTimeEnd', CHECK_IN_TIME_END_FLEXIBLE, {
          shouldDirty: true,
        });
      }
    }
  }, [bookingCheckInTime, checkInTimeEnd, isStartTimeFlexible, setValue]);

  const checkInTimeEndOptions = useMemo(() => {
    const flexibleOption = {
      label: t(
        'pageProperty.mainSettings.airbnbSettings.checkInTimeEndFlexible',
      ),
      value: CHECK_IN_TIME_END_FLEXIBLE,
    };

    if (isStartTimeFlexible) {
      return [flexibleOption];
    }

    const minEndTime = (bookingCheckInTime || 15) + 2;
    const maxEndTime = 26;

    const timeOptions = range(minEndTime, maxEndTime + 1).map((value) => ({
      label: `${value}`,
      value,
    }));

    return [flexibleOption, ...timeOptions];
  }, [isStartTimeFlexible, bookingCheckInTime, t]);

  return (
    <SelectField
      label={
        <PropertyFormFieldLabel name="airbnbSettings.checkInTimeEnd">
          {t('pageProperty.mainSettings.airbnbSettings.checkInTimeEnd')}
        </PropertyFormFieldLabel>
      }
      name="airbnbSettings.checkInTimeEnd"
      options={checkInTimeEndOptions}
      colSmInput={7}
      colSmLabel={5}
      disabled={isStartTimeFlexible}
      rightTooltip={t(
        'pageProperty.mainSettings.airbnbSettings.checkInTimeEndTooltip',
      )}
    />
  );
};

export default CheckInTimeEndField;
